/**
 * Offline Cache using IndexedDB
 *
 * Provides persistent storage for player profiles and other data
 * for true offline support across browser sessions.
 *
 * Per plan section 2c (Offline/stale data support) and Phase C.5.
 */

import { CACHE_VERSION, type CacheEntry } from './dataFreshness';

const DB_NAME = 'statscout_offline_cache';
const DB_VERSION = 1;
const STORE_NAME = 'cached_data';

// Max age for cached data (7 days in milliseconds)
const MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000;

let db: IDBDatabase | null = null;

/**
 * Initialize the IndexedDB database
 */
export async function initOfflineCache(): Promise<boolean> {
  if (!('indexedDB' in window)) {
    console.warn('IndexedDB not supported - offline caching disabled');
    return false;
  }

  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open offline cache database');
      resolve(false);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(true);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Create object store for cached data
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Get data from cache
 */
export async function getCachedData<T>(key: string): Promise<CacheEntry<T> | null> {
  if (!db) {
    const initialized = await initOfflineCache();
    if (!initialized) return null;
  }

  return new Promise((resolve) => {
    if (!db) {
      resolve(null);
      return;
    }

    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = () => {
      const result = request.result;
      if (!result) {
        resolve(null);
        return;
      }

      // Check if cache entry is expired or wrong version
      const age = Date.now() - result.timestamp;
      if (age > MAX_CACHE_AGE || result.version !== CACHE_VERSION) {
        // Expired or old version - delete and return null
        deleteCachedData(key);
        resolve(null);
        return;
      }

      resolve({
        data: result.data,
        timestamp: result.timestamp,
        version: result.version,
      });
    };

    request.onerror = () => {
      console.error('Failed to get cached data', key);
      resolve(null);
    };
  });
}

/**
 * Store data in cache
 */
export async function setCachedData<T>(key: string, data: T): Promise<boolean> {
  if (!db) {
    const initialized = await initOfflineCache();
    if (!initialized) return false;
  }

  return new Promise((resolve) => {
    if (!db) {
      resolve(false);
      return;
    }

    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const entry = {
      key,
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };

    const request = store.put(entry);

    request.onsuccess = () => {
      resolve(true);
    };

    request.onerror = () => {
      console.error('Failed to cache data', key);
      resolve(false);
    };
  });
}

/**
 * Delete data from cache
 */
export async function deleteCachedData(key: string): Promise<boolean> {
  if (!db) {
    return false;
  }

  return new Promise((resolve) => {
    if (!db) {
      resolve(false);
      return;
    }

    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(key);

    request.onsuccess = () => {
      resolve(true);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
}

/**
 * Clear all expired entries from cache
 */
export async function cleanupExpiredCache(): Promise<number> {
  if (!db) {
    return 0;
  }

  return new Promise((resolve) => {
    if (!db) {
      resolve(0);
      return;
    }

    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('timestamp');

    const cutoff = Date.now() - MAX_CACHE_AGE;
    const range = IDBKeyRange.upperBound(cutoff);
    const request = index.openCursor(range);

    let deletedCount = 0;

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        deletedCount++;
        cursor.continue();
      } else {
        resolve(deletedCount);
      }
    };

    request.onerror = () => {
      resolve(deletedCount);
    };
  });
}

/**
 * Clear all cached data
 */
export async function clearAllCache(): Promise<boolean> {
  if (!db) {
    return false;
  }

  return new Promise((resolve) => {
    if (!db) {
      resolve(false);
      return;
    }

    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => {
      resolve(true);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{ count: number; oldestTimestamp: number | null }> {
  if (!db) {
    return { count: 0, oldestTimestamp: null };
  }

  return new Promise((resolve) => {
    if (!db) {
      resolve({ count: 0, oldestTimestamp: null });
      return;
    }

    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    let count = 0;
    let oldestTimestamp: number | null = null;

    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        count++;
        const timestamp = cursor.value.timestamp;
        if (oldestTimestamp === null || timestamp < oldestTimestamp) {
          oldestTimestamp = timestamp;
        }
        cursor.continue();
      } else {
        resolve({ count, oldestTimestamp });
      }
    };

    request.onerror = () => {
      resolve({ count: 0, oldestTimestamp: null });
    };
  });
}

// Initialize cache on module load
initOfflineCache();
